import { atoms } from '@ui-lib/css';
import classNames from 'classnames';
import type { Variants } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  type MouseEventHandler,
  type ReactNode,
  type ReactPortal,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import * as styles from './Modal.css';

/**
 * Locks scroll on document body on mount
 *
 * Restores scroll lock on umount
 */
const useScrollLock = () => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  });
};

const Backdrop = ({
  position,
  blurBackground,
  withinContainer,
  children,
  onClick,
  onMouseDown,
  className,
}: {
  position?: 'bottom' | 'center';
  blurBackground?: boolean;
  withinContainer?: boolean;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  className?: string;
}) => {
  useScrollLock();
  return (
    <motion.div
      onClick={onClick}
      onMouseDown={(e) => {
        onMouseDown?.(e);
        e.stopPropagation();
      }}
      className={classNames(
        styles.backdrop({ blurBackground, withinContainer }),
        position === 'bottom' ? styles.bottomPosition : styles.centerPosition,
        atoms({
          zIndex: 'modal',
        }),
        className,
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        ease: 'easeInOut',
        duration: 0.1,
      }}
    >
      {children}
    </motion.div>
  );
};

export const ModalContainer = ({ children }: { children: ReactNode }): JSX.Element => (
  <AnimatePresence initial={false} mode="wait">
    {children}
  </AnimatePresence>
);

const slide: Variants = {
  hidden: {
    y: '100%',
    opacity: 0,
    scale: 1,
  },
  visible: {
    y: '0%',
    opacity: 1,
    scale: 1,
    transition: {
      duration: 200,
      type: 'spring',
      damping: 37,
      stiffness: 500,
    },
  },
  exit: {
    y: '100%',
    opacity: 0,
    scale: 1,
  },
};

const scale: Variants = {
  hidden: {
    scale: 0.95,
    opacity: 0,
    y: '0%',
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: '0%',
    transition: {
      duration: 200,
      type: 'spring',
      damping: 37,
      stiffness: 500,
    },
  },
  exit: {
    scale: 0.95,
    opacity: 0,
    y: '0%',
  },
};

export const Modal = ({
  open,
  children,
  handleClose,
  onExitCompleted,
  position,
  blurBackground,
  className,
  containerRef,
}: {
  open: boolean;
  children: ReactNode;
  handleClose?: () => void;
  onExitCompleted?: () => void;
  position?: 'bottom' | 'center';
  blurBackground?: boolean;
  className?: string;
  containerRef?: RefObject<HTMLElement | null>;
}): ReactPortal | null => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [isMouseDownInside, setIsMouseDownInside] = useState(false);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (modalRef.current?.contains(event.target as Node)) {
      setIsMouseDownInside(true);
      return;
    }

    setIsMouseDownInside(false);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleBackdropClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!isMouseDownInside) {
      event.preventDefault();
      event.stopPropagation();
      handleClose?.();
    }
  };

  // Call handleClose on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    return handleClose;
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  const portalContainer = containerRef?.current ?? document.body;
  const isWithinContainer = portalContainer !== document.body;

  return createPortal(
    <AnimatePresence initial={false} mode="wait" onExitComplete={onExitCompleted}>
      {open && (
        <Backdrop
          position={position}
          onClick={handleBackdropClick}
          onMouseDown={handleMouseDown}
          blurBackground={blurBackground}
          className={className}
          withinContainer={isWithinContainer}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            variants={position === 'bottom' ? slide : scale}
            className={position === 'bottom' ? styles.fullWidth : undefined}
            initial="hidden"
            animate="visible"
            exit="exit"
            ref={modalRef}
          >
            {children}
          </motion.div>
        </Backdrop>
      )}
    </AnimatePresence>,
    portalContainer,
  );
};
